import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Stack } from '@mui/material';
import type { CloneDetail, TenantAnswers } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import { ErrorState, SectionCard, SkeletonRows } from '../../components/common';
import { TenantForm } from '../../components/forms';
import { LoadingButton } from '../../components/LoadingButton';
import { useNotify } from '../../components/Notify';
import { EMPTY_TENANT, tenantErrors } from '../../lib/validate';
import { useIsOwner } from '../../auth/AuthProvider';

export function TenantTab({ clone: c }: { clone: CloneDetail }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const owner = useIsOwner();
  const tq = useQuery({ queryKey: qk.tenant(c.id), queryFn: () => endpoints.tenant(c.id) });
  const [t, setT] = useState<TenantAnswers>(EMPTY_TENANT);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (tq.data) setT(tq.data);
    else if (tq.isSuccess) setT({ ...EMPTY_TENANT, brandName: c.name, adminLogin: `admin@${c.domain}` });
  }, [tq.data, tq.isSuccess, c.name, c.domain]);
  const draft = c.status === 'draft';
  const errors = tenantErrors(t);
  const save = useMutation({
    mutationFn: () => endpoints.putTenant(c.id, t),
    onSuccess: (d) => {
      qc.setQueryData(qk.tenant(c.id), d);
      void qc.invalidateQueries({ queryKey: qk.clone(c.id) });
      notify.ok('Tenant answers saved');
    },
    onError: (e) => notify.error(e),
  });

  if (tq.isLoading) return <SkeletonRows rows={6} height={48} />;
  if (tq.isError) return <ErrorState error={tq.error} onRetry={() => void tq.refetch()} />;

  return (
    <Stack spacing={2} sx={{ maxWidth: 960 }}>
      {!draft && (
        <Alert severity="info">
          These answers bootstrapped the tenant during provisioning. They are read-only now; change brand, prices and cities in the clone's CRM.
        </Alert>
      )}
      <SectionCard>
        <TenantForm value={t} onChange={(v) => (setT(v), setTouched(true))} errors={errors} disabled={!draft || !owner} showErrors={touched || !!tq.data} />
      </SectionCard>
      {draft && owner && (
        <Box>
          <LoadingButton variant="contained" loading={save.isPending} disabled={Object.keys(errors).length > 0} onClick={() => save.mutate()}>
            Save
          </LoadingButton>
        </Box>
      )}
    </Stack>
  );
}
